import React, { useState } from "react";
import { Grid, Typography, Stack, Button, Paper, Divider } from "@mui/material";
import { Link } from "react-router-dom";
import {
  Meeting,
  PatientMeetingData,
  PatientSummary,
} from "../../../types";
import { RouteType } from "../../../router";
import {
  EditMeetingModal,
  MeetingInfoModal,
} from "../../../components/meeting-management";
import PatientChip from "../../../components/chips/patient-chip";
import PatientMeetingSummaryTile from "./patient-meeting-summary-tile";
import { formatLongDate } from "./patient-meeting-date-format";
import CopyPatientIdButton from "./copy-patient-id-button";
import EditablePatientStatusChip from "./editable-patient-status-chip";

interface PatientCardProps {
  patient: PatientSummary;
}

/**
 * PatientCard component displays a summary of a patient's information,
 * including their last and next meetings, and provides a link to view all meetings.
 */
const PatientCard: React.FC<PatientCardProps> = ({ patient }) => {
  const [selectedMeeting, setSelectedMeeting] =
    useState<PatientMeetingData | null>(null);
  const [editingMeetingId, setEditingMeetingId] = useState<string | null>(null);

  const closeMeetingModal = () => {
    setSelectedMeeting(null);
  };

  const openEditMeetingModal = (meeting: Meeting) => {
    setEditingMeetingId(meeting.meetingId);
  };

  const closeEditMeetingModal = () => {
    setEditingMeetingId(null);
  };

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
      <Paper
        sx={{
          p: 2,
          borderRadius: "0.5rem",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            boxShadow: 4,
          },
        }}
      >
        <Stack spacing={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <PatientChip label={`${patient.displayId}`} size="medium" />
              <CopyPatientIdButton patientId={patient.displayId} />
            </Stack>

            <EditablePatientStatusChip patient={patient} />
          </Stack>

          <Divider sx={{ my: 1 }} />

          <Stack direction={"row"} alignItems="center" spacing={1}>
            <PatientMeetingSummaryTile
              title="Last Meeting"
              isLoading={false}
              meeting={patient.lastMeeting}
              emptyLabel="No Past Meetings"
              onClick={(meeting) => setSelectedMeeting(meeting)}
            />

            <PatientMeetingSummaryTile
              title="Next Meeting"
              isLoading={false}
              meeting={patient.nextMeeting}
              emptyLabel="Nothing Scheduled"
              onClick={(meeting) => setSelectedMeeting(meeting)}
            />
          </Stack>

          <Divider sx={{ my: 1 }} />

          <Button
            variant="contained"
            size="small"
            fullWidth
            component={Link}
            to={`${RouteType.Patient}?patientId=${patient.patientId}`}
            sx={{
              mt: 1,
              fontSize: "0.8rem",
              fontWeight: 600,
              py: 0.5,
              borderRadius: 999,
            }}
          >
            View All Meetings
          </Button>

          <Divider sx={{ my: 1 }} />
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="center"
            sx={{ pt: 0.5 }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: "0.7rem",
                lineHeight: 1.1,
              }}
            >
              Created on the {formatLongDate(new Date(patient.created))}
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      {selectedMeeting && (
        <MeetingInfoModal
          open={selectedMeeting != null}
          handleClose={closeMeetingModal}
          meetingId={selectedMeeting.meetingId}
          onEditMeeting={openEditMeetingModal}
        />
      )}

      {editingMeetingId && (
        <EditMeetingModal
          open={editingMeetingId != null}
          handleClose={closeEditMeetingModal}
          meetingId={editingMeetingId}
        />
      )}
    </Grid>
  );
};

export default PatientCard;
